import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import { api } from '../../lib/api';

type Session = {
  id: string;
  driverName: string;
  trackName: string;
  recordedAtMs: number | null;
  lapCount: number;
  bestLapMs: number | null;
};
type Pairing = { station: { id: string; name: string }; sessions: Session[] };

function lap(ms: number | null) {
  if (ms == null) return 'No lap time';
  return `${Math.floor(ms / 60_000)}:${((ms % 60_000) / 1000).toFixed(3).padStart(6, '0')}`;
}

export function KioskPairPage() {
  const { token = '' } = useParams();
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ data: Pairing }>(`/kiosk/pair/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setPairing(data.data);
        setSelectedId(data.data.sessions[0]?.id ?? null);
      })
      .catch(() => setError('This pairing code has expired. Scan the monitor again.'));
  }, [token]);

  const selected = useMemo(
    () => pairing?.sessions.find((session) => session.id === selectedId) ?? null,
    [pairing, selectedId],
  );
  async function display() {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post<{ data: { confirmationCode: string } }>(
        `/kiosk/pair/${encodeURIComponent(token)}/display`,
        { dataFileId: selectedId },
      );
      setConfirmationCode(response.data.data.confirmationCode ?? null);
      setComplete(true);
    } catch {
      setError('Unable to prepare that session. Scan the monitor and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (error && !pairing)
    return (
      <Page>
        <Alert severity="warning">{error}</Alert>
      </Page>
    );
  if (!pairing)
    return (
      <Page>
        <CircularProgress />
        <Typography>Connecting to display…</Typography>
      </Page>
    );
  if (complete)
    return (
      <Page>
        <Typography variant="overline">SESSION READY</Typography>
        <Typography variant="h3" fontWeight={900}>
          {selected?.driverName}
        </Typography>
        <Typography variant="h5">{selected?.trackName}</Typography>
        <Typography textAlign="center">Enter this confirmation code on the kiosk:</Typography>
        <Typography variant="h2" fontWeight={900} letterSpacing="0.12em">
          {confirmationCode}
        </Typography>
        <Typography textAlign="center" color="text.secondary">
          This code verifies who you are. Enter it on the kiosk and the exact session will open
          there in your Paddiq account—no second scan needed.
        </Typography>
        <Button variant="contained" href="/">
          DONE
        </Button>
      </Page>
    );

  return (
    <Page>
      <Typography variant="overline">PREPARE SESSION FOR</Typography>
      <Typography variant="h4" fontWeight={900}>
        {pairing.station.name}
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!choosing && selected ? (
        <Box
          sx={{ width: '100%', p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
        >
          <Typography variant="overline">YOUR LATEST SESSION</Typography>
          <Typography variant="h5" fontWeight={800}>
            {selected.trackName}
          </Typography>
          <Typography color="text.secondary">
            {selected.driverName} · {selected.lapCount} laps · Best {lap(selected.bestLapMs)}
          </Typography>
        </Box>
      ) : (
        <List
          sx={{
            width: '100%',
            maxHeight: 420,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          {pairing.sessions.map((session) => (
            <ListItemButton
              key={session.id}
              selected={session.id === selectedId}
              onClick={() => setSelectedId(session.id)}
            >
              <ListItemText
                primary={session.trackName}
                secondary={`${session.driverName} · ${session.lapCount} laps · Best ${lap(session.bestLapMs)}`}
              />
            </ListItemButton>
          ))}
        </List>
      )}
      {selected ? (
        <Button
          fullWidth
          size="large"
          variant="contained"
          disabled={submitting}
          onClick={() => void display()}
        >
          {submitting ? 'PREPARING…' : 'PREPARE FOR KIOSK'}
        </Button>
      ) : (
        <Alert severity="info">No sessions are available to display.</Alert>
      )}
      {!choosing && pairing.sessions.length > 1 && (
        <Button onClick={() => setChoosing(true)}>Choose another session</Button>
      )}
    </Page>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <Stack spacing={2.5} alignItems="center" sx={{ maxWidth: 540, mx: 'auto', px: 2, py: 6 }}>
      {children}
    </Stack>
  );
}
